import { environment } from '@envs/environment'
import { accentColorConfig } from '@hooks/useAccentColor'
import { Message } from '@typings/dify'
import { AccentColor } from '@typings/index'
import 'katex/dist/katex.min.css'
import { Check, Copy, FileTextIcon, MessageSquareQuote, ThumbsUp } from 'lucide-react'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Markdown from 'react-markdown'
import rehypeKatex from 'rehype-katex'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'

interface MessageBubbleProps {
  message: Message
  onLike?: (messageId: string) => void
  accentColor: AccentColor
  onQuoteMessage?: () => void
  index: number
}

const MessageBubble = ({ message, onLike, accentColor, onQuoteMessage, index }: MessageBubbleProps) => {
  const [copied, setCopied] = useState(false)
  const { t } = useTranslation()
  const messageRef = useRef<HTMLDivElement | null>(null)

  const handleCopy = async () => {
    try {
      // Get the outer HTML of the element
      const htmlContent = messageRef.current?.outerHTML

      if (!htmlContent) {
        console.error('No content to copy.')
        return
      }

      // Create a Blob with the HTML content
      const blob = new Blob([htmlContent], { type: 'text/html' })

      // Create a ClipboardItem
      const clipboardItem = new ClipboardItem({ 'text/html': blob })

      // Copy to clipboard
      await navigator.clipboard.write([clipboardItem])

      // Provide feedback
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy HTML:', error)
    }
  }

  const processContent = (content: string) => {
    // Handle tabs
    content = content
      .replace(/\\\[/g, '$$') // Convert \[ to $$
      .replace(/\\\]/g, '$$') // Convert \] to $$
      .replace(/\\\(/g, '$') // Convert \( to $
      .replace(/\\\)/g, '$') // Convert \) to $

    return content
  }

  return (
    <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className="group relative max-w-[80%]">
        <div
          className={`p-4 rounded-lg ${
            message.role === 'user'
              ? `${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].primary} ${accentColorConfig[accentColor].primary.includes('text-black') ? '' : 'text-white'}`
              : 'bg-gray-100 dark:bg-gray-800 dark:text-white'
          }`}
        >
          <div ref={messageRef}>
            <Markdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeKatex]}
              components={{
                h1: (props) => <h1 className="text-xl font-bold" {...props} />,
                h2: (props) => <h2 className="text-lg font-bold" {...props} />,
                h3: (props) => <h3 className="text-base font-bold" {...props} />,
                p: (props) => <p className="my-2" {...props} />,
                a: (props) => <a className="text-blue-500" {...props} />,
                li: (props) => <li className="ml-4 my-2" {...props} />,
                table: (props) => (
                  <table className="table-auto border-collapse border border-gray-300 w-full my-4" {...props} />
                ),
                thead: (props) => <thead className="bg-gray-300 dark:bg-gray-900" {...props} />,
                tbody: (props) => <tbody {...props} />,
                tr: (props) => <tr {...props} />,
                th: (props) => <th className="border border-gray-300 px-4 py-2 text-left font-bold" {...props} />,
                td: (props) => <td className="border border-gray-300 px-4 py-2" {...props} />,
                code: (props) => <code className="bg-gray-900 text-white rounded-md px-2 py-1 text-sm" {...props} />,
                pre: (props) => (
                  <pre className="bg-gray-900 text-white rounded-md px-4 py-3 overflow-x-auto mb-2 mt-2" {...props} />
                ),
                img: (props) => <img className="max-w-full" {...props} />,
                hr: (props) => <hr className="my-4" {...props} />,
                strong: (props) => <strong className="font-bold" {...props} />,
                em: (props) => <em className="italic" {...props} />,
                del: (props) => <del className="text-red-500" {...props} />,
                blockquote: (props) => <blockquote className="border-l-4 border-gray-300 pl-4 my-2" {...props} />,
                ul: (props) => <ul className="list-disc my-2" {...props} />,
                ol: (props) => <ol className="list-decimal my-2" {...props} />,
              }}
            >
              {processContent(message.content)}
            </Markdown>

          {message.files && message.files.length > 0 && (
            <div className="flex flex-row">
              {message.files.map((file) => (
                <div
                  key={file.url}
                  className="flex items-center mt-1 ml-1 border-dashed border border-gray-400 text-gray-500 dark:text-gray-300 p-2 py-1 rounded-2xl w-64 mb-5 max-w-[80%]"
                >   
                    <FileTextIcon size={20} />
                    <span className="ml-1 truncate max-w-full">{file.filename}</span>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {message.role === 'assistant' && (
          <div className="flex items-center justify-start mt-2 ml-2 space-x-2 bg-gray-200 p-1 px-2 rounded-full w-fit">
            <button
              onClick={handleCopy}
              className={`p-1 hover:${accentColorConfig[accentColor].text} bg-white rounded-full shadow-sm`}
              title={t('chat.copy')}
            >
              {copied ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
            </button>
            {onLike && (
              <button
                onClick={() => onLike(message.id)}
                className={`p-1 bg-white rounded-full shadow-sm ${
                  message.liked ? 'text-yellow-500' : 'hover:text-yellow-500'
                }`}
                title={t('chat.like')}
              >
                <ThumbsUp size={16} className={message.liked ? 'fill-current' : ''} />
              </button>
            )}
            <button
              onClick={onQuoteMessage}
              className={`p-1 hover:${accentColorConfig[accentColor].text} bg-white rounded-full shadow-sm`}
              title={t('chat.quote')}
            >
              <MessageSquareQuote size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default MessageBubble
